import {FC, RefObject, useEffect, useRef, useState} from "react";
import {Dropdown} from "semantic-ui-react";
import {DataStatus} from "@/features/application/api/applicationModels";
import {IProblemDetails} from "@/features/application/api/applicationModels";
import {
    ErrorMessage,
    InfoMessage,
    LoadingMessage
} from "@/components/SystemMessages";
import {getXDaysAgo} from "@/util/dateUtil";
import driveThruReplayAPI from "../api/driveThruReplayAPI";
import {ICamera, ICameraFeedResponse} from "../api/driveThruReplayModels";

interface IProps {
    locationPk: number;
    businessDate: string;
    /** SOS lane entry (seconds since business-day midnight). */
    selectedSecondOfDay: number | null;
    /** Total SOS visit length in seconds; sizes the Envysion embed window. */
    selectedTotalSec: number | null;
}

const NUM_CAMERAS = 3;
// Fixed base size for the Envysion iframe. Not the on-screen size — the feed is
// responsive and rescales with its slot (see useFeedTransform). An <iframe>
// can't `object-fit: cover` its embedded content, so we give it a fixed-ratio
// size and CSS-scale() it to fill the slot. Only the 4:3 ratio matters; the
// pixel values are an arbitrary base for the scale math.
const CAMERA_ASPECT_RATIO = 4 / 3;
const CAMERA_EMBED_WIDTH = 960;
const CAMERA_EMBED_HEIGHT = CAMERA_EMBED_WIDTH / CAMERA_ASPECT_RATIO;
const CAMERA_FOOTAGE_RETENTION_DAYS = 21;

const isBusinessDateBeyondCameraRetention = (businessDate: string): boolean => {
    const datePart = businessDate.split("T")[0];
    return datePart < getXDaysAgo(CAMERA_FOOTAGE_RETENTION_DAYS);
};

interface IFeedTransform {
    scale: number;
    x: number;
    y: number;
}

const useFeedTransform = (feedRef: RefObject<HTMLDivElement | null>) => {
    const [transform, setTransform] = useState<IFeedTransform>({
        scale: 1,
        x: 0,
        y: 0
    });

    useEffect(() => {
        const el = feedRef.current;
        if (!el) return;

        const updateTransform = () => {
            const width = el.clientWidth;
            const height = el.clientHeight;
            if (width <= 0 || height <= 0) return;

            const scale = Math.max(
                width / CAMERA_EMBED_WIDTH,
                height / CAMERA_EMBED_HEIGHT
            );
            const scaledWidth = CAMERA_EMBED_WIDTH * scale;
            const scaledHeight = CAMERA_EMBED_HEIGHT * scale;

            setTransform({
                scale,
                x: (width - scaledWidth) / 2,
                y: (height - scaledHeight) / 2
            });
        };

        updateTransform();
        const observer = new ResizeObserver(updateTransform);
        observer.observe(el);
        window.addEventListener("resize", updateTransform);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateTransform);
        };
    }, [feedRef]);

    return transform;
};

interface ICameraFeedProps {
    slotIndex: number;
    embedUrl: string;
    remountKey: number;
}

const CameraFeed: FC<ICameraFeedProps> = ({slotIndex, embedUrl, remountKey}) => {
    const feedRef = useRef<HTMLDivElement>(null);
    const transform = useFeedTransform(feedRef);

    return (
        <div ref={feedRef} className="drive-thru-replay-cam-feed">
            <iframe
                key={`${embedUrl}-${remountKey}`}
                src={embedUrl}
                title={`Camera feed ${slotIndex + 1}`}
                className="drive-thru-replay-cam-iframe"
                scrolling="no"
                allow="autoplay"
                allowFullScreen
                style={{
                    width: CAMERA_EMBED_WIDTH,
                    height: CAMERA_EMBED_HEIGHT,
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`
                }}
            />
        </div>
    );
};

const DriveThruVideoPlayer: FC<IProps> = ({
    locationPk,
    businessDate,
    selectedSecondOfDay,
    selectedTotalSec
}) => {
    const [feedStatus, setFeedStatus] = useState<DataStatus>(DataStatus.Undefined);
    const [feed, setFeed] = useState<ICameraFeedResponse | null>(null);
    const [feedError, setFeedError] = useState<IProblemDetails | null>(null);
    const [cameraSelections, setCameraSelections] = useState<(string | null)[]>(() =>
        Array<string | null>(NUM_CAMERAS).fill(null)
    );
    const [cameraRemountKeys, setCameraRemountKeys] = useState<number[]>(() =>
        Array<number>(NUM_CAMERAS).fill(0)
    );
    const abortRef = useRef<AbortController | null>(null);
    const cameraSelectionsInitialized = useRef(false);
    const ssoAutoOpenedRef = useRef(false);
    const [ssoIframeUrl, setSsoIframeUrl] = useState<string | null>(null);
    // True when cameras are safe to render (either SSO completed or not needed).
    // Starts true so the empty pre-load state renders normally.
    const [ssoReady, setSsoReady] = useState(true);

    // *** FOR TESTING ONLY ***
    // Uses yesterday's date so Envysion feed requests land on a date with
    // known camera footage, while still using the transaction's time-of-day.
    // Remove the override below and restore `businessDate` before shipping.
    // const yesterday = new Date();
    // yesterday.setDate(yesterday.getDate() - 1);
    // const yesterdayISO = yesterday.toISOString().slice(0, 10); // "YYYY-MM-DD"
    // const cameraFeedDate = yesterdayISO || businessDate;
    // *** END TESTING ***

    // For production, use the transaction's business date as the feed date
    const cameraFeedDate = businessDate;

    const footageUnavailable = isBusinessDateBeyondCameraRetention(cameraFeedDate);

    useEffect(() => {
        if (selectedSecondOfDay === null || footageUnavailable) {
            abortRef.current?.abort();
            setFeed(null);
            setFeedStatus(DataStatus.Undefined);
            setFeedError(null);
            setSsoIframeUrl(null);
            setSsoReady(true);
            ssoAutoOpenedRef.current = false;
            setCameraSelections(Array<string | null>(NUM_CAMERAS).fill(null));
            cameraSelectionsInitialized.current = false;
            return;
        }

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setFeedStatus(DataStatus.Loading);
        setFeed(null);
        setFeedError(null);
        setCameraRemountKeys(Array<number>(NUM_CAMERAS).fill(0));

        // Window start/end computed in EnvysionVideoWindowCalculator (server).
        driveThruReplayAPI
            .getCameraFeed(
                locationPk,
                cameraFeedDate,
                selectedSecondOfDay,
                selectedTotalSec,
                controller.signal
            )
            .then((result) => {
                if (result) {
                    setFeed(result);
                    if (!cameraSelectionsInitialized.current) {
                        const cameras = result.cameras;
                        setCameraSelections(
                            Array.from(
                                {length: NUM_CAMERAS},
                                (_, i) => cameras[i]?.id ?? null
                            )
                        );
                        cameraSelectionsInitialized.current = true;
                    }
                    setFeedStatus(DataStatus.Loaded);
                    // Silently authenticate via a hidden iframe so cameras
                    // play without any popup or user action.
                    if (!ssoAutoOpenedRef.current && result.ssoLoginUrl) {
                        ssoAutoOpenedRef.current = true;
                        setSsoReady(false); // hold cameras until SSO completes
                        setSsoIframeUrl(result.ssoLoginUrl);
                    } else {
                        setSsoReady(true);
                    }
                } else {
                    setFeedStatus(DataStatus.Failed);
                }
            })
            .catch((err: unknown) => {
                if (err instanceof Error && err.name === "AbortError") return;
                setFeedError(
                    typeof err === "object" && err !== null && "detail" in err
                        ? (err as IProblemDetails)
                        : null
                );
                setFeedStatus(DataStatus.Failed);
            });

        return () => controller.abort();
    }, [
        selectedSecondOfDay,
        selectedTotalSec,
        locationPk,
        cameraFeedDate,
        footageUnavailable
    ]);

    const buildEmbedUrl = (feed: ICameraFeedResponse, cameraId: string): string =>
        `${feed.videoBaseUrl}?cameraId=${encodeURIComponent(
            cameraId
        )}&startDateTime=${encodeURIComponent(
            feed.startDateTime
        )}&endDateTime=${encodeURIComponent(feed.endDateTime)}`;

    const cameraOptions = (cameras: ICamera[]) =>
        cameras.map((c) => ({key: c.id, value: c.id, text: c.name}));

    const updateCameraSelection = (slotIndex: number, cameraId: string) => {
        setCameraSelections((prev) => {
            const next = [...prev];
            next[slotIndex] = cameraId;
            return next;
        });
    };

    if (footageUnavailable) {
        return (
            <div className="drive-thru-replay-video-panel">
                <InfoMessage
                    inline
                    text={`Camera footage is only available for the last ${CAMERA_FOOTAGE_RETENTION_DAYS} days. This transaction is outside that window.`}
                />
            </div>
        );
    }

    if (feedStatus === DataStatus.Loading || !ssoReady) {
        return (
            <div className="drive-thru-replay-video-panel">
                {ssoIframeUrl && (
                    <iframe
                        src={ssoIframeUrl}
                        title="Envysion SSO"
                        style={{display: "none"}}
                        onLoad={() => {
                            setSsoIframeUrl(null);
                            setSsoReady(true);
                        }}
                    />
                )}
                <LoadingMessage />
            </div>
        );
    }

    if (feedStatus === DataStatus.Failed) {
        const isUnavailable = feedError?.status === 503;
        return (
            <div className="drive-thru-replay-video-panel">
                {isUnavailable ? (
                    <InfoMessage inline text={feedError?.detail ?? ""} />
                ) : (
                    <ErrorMessage inline text={feedError?.detail ?? ""} />
                )}
            </div>
        );
    }

    return (
        <div className="drive-thru-replay-video-panel">
            <div className="drive-thru-replay-camera-grid">
                {Array.from({length: NUM_CAMERAS}, (_, slotIndex) => {
                    const cameraId = cameraSelections[slotIndex];
                    const embedUrl =
                        feed && cameraId ? buildEmbedUrl(feed, cameraId) : null;

                    return (
                        <div key={slotIndex} className="drive-thru-replay-cam-slot">
                            {embedUrl ? (
                                <CameraFeed
                                    slotIndex={slotIndex}
                                    embedUrl={embedUrl}
                                    remountKey={cameraRemountKeys[slotIndex]}
                                />
                            ) : (
                                <div className="drive-thru-replay-cam-feed" />
                            )}
                            {feed && (
                                <div className="drive-thru-replay-cam-control-slot">
                                    <Dropdown
                                        selection
                                        fluid
                                        upward
                                        options={cameraOptions(feed.cameras)}
                                        value={
                                            cameraSelections[slotIndex] ?? undefined
                                        }
                                        onChange={(_e, {value}) =>
                                            updateCameraSelection(
                                                slotIndex,
                                                value as string
                                            )
                                        }
                                        placeholder="Select camera"
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DriveThruVideoPlayer;
